import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { productService } from "../../services/productService";
import { stockService } from "../../services/stockService";
import { Product, CreateProductRequest, SearchParams } from "../../types";
import type { StockInfo as ApiStockInfo } from "../../services/stockService";

// Define the initial state interface
interface ProductsState {
    products: Product[];
    selectedProduct: Product | null;
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;
    searchParams: SearchParams;
    totalCount: number;
}

// Define the initial state
const initialState: ProductsState = {
    products: [],
    selectedProduct: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    searchParams: { skip: 0, limit: 100 },
    totalCount: 0,
};

// Helper function to merge stock info with products
const mergeProductsWithStock = async (
    products: Product[],
): Promise<Product[]> => {
    const stockPromises = products.map((product) =>
        stockService.getProductStock(product.product_id),
    );

    const stockResults = await Promise.allSettled(stockPromises);

    return products.map((product, index) => {
        const stockResult = stockResults[index];
        if (stockResult.status === "fulfilled" && stockResult.value) {
            const stockInfo = stockResult.value as ApiStockInfo;
            return {
                ...product,
                stock: {
                    quantity: Number(stockInfo.quantity ?? 0),
                    reserved_quantity: Number(stockInfo.reserved_quantity ?? 0),
                    min_stock_level: stockInfo.min_stock_level || 10,
                    max_stock_level: stockInfo.max_stock_level || 1000,
                    last_updated: new Date().toISOString(),
                },
            };
        }
        return product;
    });
};

// Async thunks
export const fetchProducts = createAsyncThunk(
    "products/fetchProducts",
    async (params: SearchParams = {}, { rejectWithValue }) => {
        try {
            const response = await productService.getProducts(params);
            const productsWithStock = await mergeProductsWithStock(
                response.items,
            );
            return {
                products: productsWithStock,
                total: response.total,
                params,
            };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch products");
        }
    },
);

export const fetchProductById = createAsyncThunk(
    "products/fetchProductById",
    async (productId: string, { rejectWithValue }) => {
        try {
            const product = await productService.getProductById(productId);
            const [productWithStock] = await mergeProductsWithStock([product]);
            return productWithStock;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch product");
        }
    },
);

export const createProduct = createAsyncThunk(
    "products/createProduct",
    async (
        productData: CreateProductRequest,
        { rejectWithValue, dispatch },
    ) => {
        try {
            const newProduct = await productService.createProduct(productData);
            // Refresh the products list
            dispatch(fetchProducts({}));
            return newProduct;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create product");
        }
    },
);

export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async (
        { id, data }: { id: string; data: Partial<Product> },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const updatedProduct = await productService.updateProduct(id, data);
            // Refresh the products list
            dispatch(fetchProducts({}));
            return updatedProduct;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update product");
        }
    },
);

export const deleteProduct = createAsyncThunk(
    "products/deleteProduct",
    async (productId: string, { rejectWithValue, dispatch }) => {
        try {
            await productService.deleteProduct(productId);
            // Refresh the products list
            dispatch(fetchProducts({}));
            return productId;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to delete product");
        }
    },
);

// Create the products slice
const productsSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
            state.selectedProduct = action.payload;
        },
        updateSearchParams: (
            state,
            action: PayloadAction<Partial<SearchParams>>,
        ) => {
            state.searchParams = { ...state.searchParams, ...action.payload };
        },
        clearSelectedProduct: (state) => {
            state.selectedProduct = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch products
            .addCase(fetchProducts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.products = action.payload.products;
                state.searchParams = {
                    ...state.searchParams,
                    ...action.payload.params,
                };
                state.totalCount = action.payload.total;
                state.error = null;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch product by ID
            .addCase(fetchProductById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedProduct = action.payload;
                state.error = null;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create product
            .addCase(createProduct.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createProduct.fulfilled, (state) => {
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createProduct.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.payload as string;
            })
            // Update product
            .addCase(updateProduct.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateProduct.fulfilled, (state) => {
                state.isUpdating = false;
                state.error = null;
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload as string;
            })
            // Delete product
            .addCase(deleteProduct.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteProduct.fulfilled, (state) => {
                state.isDeleting = false;
                state.error = null;
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.isDeleting = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions
export const {
    clearError,
    setSelectedProduct,
    updateSearchParams,
    clearSelectedProduct,
} = productsSlice.actions;

// Export selectors
export const selectProducts = (state: any) => state.products.products;
export const selectSelectedProduct = (state: any) =>
    state.products.selectedProduct;
export const selectProductsLoading = (state: any) => state.products.isLoading;
export const selectProductsError = (state: any) => state.products.error;
export const selectProductsSearchParams = (state: any) =>
    state.products.searchParams;
export const selectProductsCreating = (state: any) => state.products.isCreating;
export const selectProductsUpdating = (state: any) => state.products.isUpdating;
export const selectProductsDeleting = (state: any) => state.products.isDeleting;
export const selectProductsTotalCount = (state: any) =>
    state.products.totalCount;

// Export reducer
export default productsSlice.reducer;
